import React from 'react';
import PropTypes from 'prop-types';
import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { captureAnalytics } from '../../actions/preload';
import {
  CHART_STATUS_LABELS,
  getChartStatusForTransmission,
} from '../../containers/records/shared/chartStatus';

// One reply-level chart status chip (ENG-3012). A reply files as ONE merged
// document, so the row's scalar EMR-status flags (from the last EmrUpload)
// are unambiguous — no per-attachment status exists or should be added.
// Status derivation and wording come from the shared getChartStatusForTransmission
// helper — the same source as the inbox table icon and the viewer chip — so
// this chip can never disagree with the rest of the app about a filing.

const SEVERITY_TAGS = {
  success: { color: 'success', icon: <CheckCircleOutlined /> },
  error: { color: 'error', icon: <CloseCircleOutlined /> },
  warning: { color: 'warning', icon: <ExclamationCircleOutlined /> },
  info: { color: 'processing', icon: <SyncOutlined spin /> },
};

const ReplyChartStatusChip = ({ row }) => {
  if (!row) return null;

  const status = getChartStatusForTransmission(row, {
    orgName: localStorage.getItem('m8_org'),
  });
  if (!status) return null;

  const severityTag = SEVERITY_TAGS[status.severity] || SEVERITY_TAGS.info;
  // Queued-for-tonight is not an active sync — show a clock, not a spinner.
  const icon = status.statusKey === 'auto_upload_scheduled'
    ? <ClockCircleOutlined />
    : severityTag.icon;

  const tag = (
    <Tag icon={icon} color={severityTag.color} data-testid="reply-chart-status-chip">
      {CHART_STATUS_LABELS[status.statusKey] || 'EMR Status'}
    </Tag>
  );

  return (
    <div style={{ marginTop: 12 }}>
      <Tooltip title={status.message}>
        {status.link ? (
          <a
            href={status.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => captureAnalytics('reply_chart_status_chart_link_clicked', {
              status_key: status.statusKey,
            })}
          >
            {tag}
          </a>
        ) : tag}
      </Tooltip>
    </div>
  );
};

ReplyChartStatusChip.defaultProps = {
  row: null,
};

ReplyChartStatusChip.propTypes = {
  row: PropTypes.object,
};

export default ReplyChartStatusChip;
